import React, { useEffect } from 'react'
import dynamic from 'next/dynamic'
import usePageContext from '../../Page/context'
import { BlocksRenderer } from '@strapi/blocks-react-renderer'
import { getCloudinaryImageUrl } from '../../../../utils'
import YouTubeIFrame from '../../YoutubeIframe'

// Dynamically import Videoplayer with SSR disabled
const VideoPlayer = dynamic(() => import('../../Videoplayer'), { ssr: false })

const ModuleMedallist = ({ medallistData }: { medallistData: any }) => {

  const thereIsYoutubeVideo = medallistData.attributes?.videoYouTube
  const thereIsFileVideo = medallistData.attributes?.video?.data?.attributes?.provider_metadata?.public_id

  const {
    state: { getEnvConfig },
    dispatch
  } = usePageContext()

  const [isThemeSet, setIsThemeSet] = React.useState(false)

  const renderMedalType = () => {
    if (medallistData.attributes?.medalTypeTitle && medallistData.attributes?.medalTypeValue) {
      return `${medallistData.attributes?.medalTypeTitle}:`
    }
    if (medallistData.attributes?.medalTypeTitle && !medallistData.attributes?.medalTypeValue) {
      return `${medallistData.attributes?.medalTypeTitle}`
    }
    return null
  }

  // Set theme on mount
  useEffect(() => {
    if (!isThemeSet) {
      dispatch({
        type: 'SET_THEME',
        payload: 'black' // Keep theme setting logic
      })
      setIsThemeSet(true)
    }
  }, [isThemeSet, dispatch])

  if (!medallistData?.id) {
    return null
  }

  return (
    <div className='md:pt-[190px] pb-20 px-6 max-w-[1920px] mx-auto'>
      {/* Top Section: Text + Main Image */}
      <div className='grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-4'>
        {/* Text Column */}
        <div className='w-2/3'>
          <h1 className='font-abcd uppercase font-extrabold text-[40px] leading-[85%] tracking-tighter mb-2'>
            {medallistData.attributes?.name}
            {medallistData.attributes?.name && medallistData.attributes?.role ? ', ' : ''}
            <br />
            {medallistData.attributes?.role || ''}
          </h1>
          {medallistData.attributes?.description && (
            <div className='font-swinton font-normal text-[19px] leading-none tracking-tighter mb-0 [&>p:last-child]:mb-0'>
              <BlocksRenderer
                content={medallistData.attributes?.description}
                blocks={{
                  paragraph: ({ children }) => <p className='mb-4'>{children}</p>
                }}
              />
            </div>
          )}
          <div className="w-[25px] h-[44px] bg-[url('/svgs/ab-awards-logo-only.svg')] bg-no-repeat bg-center bg-cover my-0 mb-1 lg:mt-[45px]" />
          <h3 className='font-abcd uppercase font-extrabold text-[40px] leading-[80%] tracking-normal mb-1'>
            {renderMedalType()}
            {medallistData.attributes?.medalTypeTitle && medallistData.attributes?.medalTypeValue && <br />}
            {medallistData.attributes?.medalTypeValue || ''}
          </h3>
          <div className='font-swinton font-normal text-[19px] leading-none tracking-tighter'>
            {medallistData.attributes?.medalDescription && (
              <div className='font-swinton font-normal text-[19px] leading-none tracking-tighter mb-0 [&>p:last-child]:mb-0'>
                <BlocksRenderer
                  content={medallistData.attributes.medalDescription}
                  blocks={{
                    paragraph: ({ children }) => <p className='mb-4'>{children}</p>
                  }}
                />
              </div>
            )}
          </div>
        </div>

        {/* Image Column */}
        <div className='w-full'>
          <div className='relative aspect-[4/5] w-full flex'>
            {medallistData.attributes?.backgroundForProfileImage?.data?.attributes?.url && (
              <img
                src={getCloudinaryImageUrl(
                  medallistData.attributes?.backgroundForProfileImage?.data?.attributes?.url,
                  'ar_4:5,g_center,q_auto,dpr_auto,f_auto,w_924'
                )} // Placeholder
                alt={medallistData.attributes.name || ''}
                title={medallistData.attributes.name || ''}
              />
            )}
          </div>
        </div>
      </div>

      {/* Gallery Section */}
      <div className='grid grid-cols-1 md:grid-cols-2 md:gap-x-7'>
        {/* Gallery Item 1 (Landscape - Spans 2 cols) */}
        {medallistData?.attributes?.image1Large?.data?.attributes?.url && (
          <div className='relative aspect-[16/10] w-full md:col-span-2'>
            {medallistData.attributes?.image1Large?.data?.attributes?.formats && (
              <img
                src={getCloudinaryImageUrl(
                  medallistData?.attributes?.image1Large?.data?.attributes?.url,
                  'ar_16:10,g_center,q_auto,dpr_auto,f_auto,w_1920'
                )} // Placeholder
                alt={medallistData.attributes.name || ''}
                title={medallistData.attributes.name || ''}
              />
            )}
          </div>
        )}
        {/* Gallery Item 2 (Portrait - Left col) */}
        {medallistData.attributes?.image2Left?.data?.attributes?.url && (
          <div className='relative aspect-[4/5] w-full flex h-full'>
            <img
              src={getCloudinaryImageUrl(
                medallistData.attributes?.image2Left?.data?.attributes?.url,
                'ar_4:5,g_center,q_auto,dpr_auto,f_auto,w_924'
              )} // Placeholder
              alt={medallistData.attributes.name || ''}
            />
          </div>
        )}
        {/* Gallery Item 3 (Portrait - Right col) */}
        {medallistData.attributes?.image3Right?.data?.attributes?.url && (
          <div className='relative aspect-[4/5] w-full flex h-full'>
            <img
              src={getCloudinaryImageUrl(
                medallistData.attributes?.image3Right?.data?.attributes?.url,
                'ar_4:5,g_center,q_auto,dpr_auto,f_auto,w_924'
              )} // Placeholder
              alt={medallistData.attributes.name || ''}
            />
          </div>
        )}
        {thereIsYoutubeVideo ? <div className='relative w-full h-full md:col-span-2'>
          <YouTubeIFrame
            url={medallistData.attributes?.videoYouTube}
          />
        </div> : thereIsFileVideo ?
          <div className='relative w-full h-full md:col-span-2'>
            <VideoPlayer
              autoPlay={false} // Don't autoplay gallery video by default
              loop={false}
              controls // Show controls for gallery video
              id='medallistDetailVideoPlayer'
              publicId={medallistData.attributes?.video?.data?.attributes?.provider_metadata?.public_id} // Provided video ID
              cloudName={getEnvConfig.NEXT_CLOUDINARY_CLOUD_NAME}
              playerConfig={{
                muted: true, // Start muted
                fluid: true,
                theme: 'light',
                posterOptions: {}
              }}
            />
          </div>
          : null}
      </div>
    </div>
  )
}

export default ModuleMedallist
